import React from "react";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputMask } from "primereact/inputmask";
import { CapData, HourlyForm, HourlyWindow, PeriodForm, Weekday } from "../types";
import { minToHHmm, hhmmToMin } from "../utils";
import PeriodSection from "./PeriodSection";
import Field from "./Field";

// ─── constants ────────────────────────────────────────────────────────────────

const MAX_WINDOWS = 4;

const ALL_WEEKDAYS: { short: string; value: Weekday }[] = [
  { short: "Mo", value: "MONDAY" },
  { short: "Tu", value: "TUESDAY" },
  { short: "We", value: "WEDNESDAY" },
  { short: "Th", value: "THURSDAY" },
  { short: "Fr", value: "FRIDAY" },
  { short: "Sa", value: "SATURDAY" },
  { short: "Su", value: "SUNDAY" },
];

const DAY_LABELS: Record<Weekday, string> = {
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
  FRIDAY: "Friday",
  SATURDAY: "Saturday",
  SUNDAY: "Sunday",
};

// ─── WindowCard ───────────────────────────────────────────────────────────────

type WindowCardProps = {
  index: number;
  window: HourlyWindow;
  error: string | null;
  onChange: (w: HourlyWindow) => void;
  onRemove: () => void;
};

const WindowCard: React.FC<WindowCardProps> = ({
  index,
  window: w,
  error,
  onChange,
  onRemove,
}) => {
  const setField =
    <K extends keyof HourlyWindow>(key: K) =>
    (val: HourlyWindow[K]) =>
      onChange({ ...w, [key]: val });

  const toggleDay = (day: Weekday) => {
    const next = w.weekdays.includes(day)
      ? w.weekdays.filter((d) => d !== day)
      : [...w.weekdays, day];
    onChange({ ...w, weekdays: next });
  };

  return (
    <div className={`te-window-card${error ? " te-window-card--error" : ""}`}>
      <div className="te-window-card-header">
        <i className="pi pi-clock te-window-card-icon" />
        <span>Window {index + 1}</span>
        <button
          className="te-window-remove"
          onClick={onRemove}
          type="button"
          aria-label="Remove window"
        >
          <i className="pi pi-times" />
        </button>
      </div>

      <div className="te-window-card-body">
        {/* Weekday picker */}
        <div className="te-weekday-row">
          <span className="te-weekday-label">Days</span>
          <div className="te-weekdays">
            {ALL_WEEKDAYS.map(({ short, value }) => (
              <button
                key={value}
                type="button"
                className={`te-weekday-pill${w.weekdays.includes(value) ? " te-weekday-pill--active" : ""}`}
                onClick={() => toggleDay(value)}
              >
                {short}
              </button>
            ))}
          </div>
        </div>

        {/* Time range */}
        <div className="te-fields-grid te-fields-grid--2col">
          <Field label="Start time" unit="HH:MM">
            <InputMask
              mask="99:99"
              value={w.startTime}
              onChange={(e) => setField("startTime")(e.value ?? "")}
              className="te-input-mask w-full"
            />
          </Field>
          <Field label="End time" unit="HH:MM">
            <InputMask
              mask="99:99"
              value={w.endTime}
              onChange={(e) => setField("endTime")(e.value ?? "")}
              className="te-input-mask w-full"
            />
          </Field>
        </div>

        <div className="te-fields-divider" />

        {/* Prices */}
        <div className="te-fields-grid">
          <Field label="Energy fee" unit="€/kWh">
            <InputNumber
              value={w.energyPrice}
              onValueChange={(e) => setField("energyPrice")(e.value ?? 0)}
              min={0}
              max={1}
              minFractionDigits={2}
              maxFractionDigits={2}
              className="te-input w-full"
            />
          </Field>
          <Field label="Blocking fee" unit="€/h">
            <InputNumber
              value={w.blockingFeePrice}
              onValueChange={(e) => setField("blockingFeePrice")(e.value ?? 0)}
              min={0}
              max={24}
              minFractionDigits={2}
              maxFractionDigits={2}
              className="te-input w-full"
            />
          </Field>
          <Field label="Starts after" unit="HH:MM">
            <InputMask
              mask="99:99"
              value={minToHHmm(w.blockingFeeAfterMin)}
              onChange={(e) => setField("blockingFeeAfterMin")(hhmmToMin(e.value))}
              className="te-input-mask w-full"
            />
          </Field>
        </div>

        {error && (
          <div className="te-window-error">
            <i className="pi pi-exclamation-circle" />
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── HourlySection ────────────────────────────────────────────────────────────

type Props = {
  form: HourlyForm;
  cap: CapData;
  activationPrice: number;
  conflictingDays: Weekday[];
  windowErrors: Array<string | null>;
  onChange: (form: HourlyForm) => void;
};

const HourlySection: React.FC<Props> = ({
  form,
  cap,
  activationPrice,
  conflictingDays,
  windowErrors,
  onChange,
}) => {
  const setBase = (base: PeriodForm) => onChange({ ...form, base });

  const setWindow = (i: number) => (w: HourlyWindow) =>
    onChange({ ...form, windows: form.windows.map((old, idx) => (idx === i ? w : old)) });

  const removeWindow = (i: number) =>
    onChange({ ...form, windows: form.windows.filter((_, idx) => idx !== i) });

  const addWindow = () => {
    const w: HourlyWindow = {
      weekdays: [],
      startTime: "09:00",
      endTime: "17:00",
      energyPrice: 0.3,
      blockingFeePrice: 0,
      blockingFeeAfterMin: 60,
    };
    onChange({ ...form, windows: [...form.windows, w] });
  };

  return (
    <div className="flex flex-column gap-3">
      {/* Base tariff */}
      <div className="te-section-heading">
        <i className="pi pi-tag" />
        <span>Base Tariff</span>
        <span className="te-section-hint">Applied when no window matches</span>
      </div>

      <PeriodSection
        icon="pi pi-tag"
        label="Base"
        accentColor="#225349"
        period={form.base}
        cap={cap}
        activationPrice={activationPrice}
        onChange={setBase}
      />

      {/* Time windows */}
      <div className="te-windows-header">
        <div className="te-section-heading">
          <i className="pi pi-clock" />
          <span>Time Windows</span>
          <span className="te-section-hint">
            Override base prices for specific times ({form.windows.length}/{MAX_WINDOWS})
          </span>
        </div>
        {form.windows.length < MAX_WINDOWS && (
          <Button
            label="Add Window"
            icon="pi pi-plus"
            size="small"
            outlined
            onClick={addWindow}
            className="te-add-window-btn"
          />
        )}
      </div>

      {form.windows.length === 0 && (
        <div className="te-windows-empty">
          No time windows — base tariff applies at all times.
        </div>
      )}

      {form.windows.map((w, i) => (
        <WindowCard
          key={i}
          index={i}
          window={w}
          error={windowErrors[i] ?? null}
          onChange={setWindow(i)}
          onRemove={() => removeWindow(i)}
        />
      ))}

      {/* Overlap error */}
      {conflictingDays.length > 0 && (
        <div className="te-overlap-error">
          <i className="pi pi-exclamation-triangle" />
          <span>
            Windows overlap on:{" "}
            {conflictingDays.map((d) => DAY_LABELS[d]).join(", ")}.
            Fix the conflicts before saving.
          </span>
        </div>
      )}
    </div>
  );
};

export default HourlySection;
